import * as react from 'react'
import { useEffect, useState } from 'react'

import { InteractionType, IPublicClientApplication } from '@azure/msal-browser'
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useMsalAuthentication,
} from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { loginRequest } from './AuthConfig'
import { useNetwork } from './context/providers/NetworkContext'
import { createSnackBar, errorSnackBar } from './services/SnackBarService'
import SessionExpiredDialog from '@/components/session-expired-dialog/SessionExpiredDialog'
import { ConfigProvider } from '@/context/providers/ConfigProvider'
import { DeviceProvider } from '@/context/providers/DeviceProvider'
import { getEnv } from '@/hooks/useConfig'
import { useIPadKeyboardHandler } from '@/hooks/useIPadKeyboardHandler'
import './App.scss'
import NetworkInterceptor from '@/services/NetworkInterceptor'

declare global {
  namespace React.JSX {
    interface IntrinsicElements {
      'system-version': {
        system: string
        env: 'dev' | 'prod'
        'more-info-url'?: string
        msalInstance: IPublicClientApplication
      }
    }
  }
}

const CanvasWrapper = react.lazy(() => import('@/pages/CanvasWrapper/CanvasWrapper'))
const Home = react.lazy(() => import('@/pages/Home/Home'))

const RELEASE_NOTES_MORE_INFO_URL =
  'https://hennesandmauritz.sharepoint.com/sites/BUASL/_layouts/15/news.aspx?title=NEWS+%26+SYSTEM+UPDATES&newsSource=3&instanceId=5e796676-5648-4de5-9034-57b06760aece&webPartId=8c88f208-6c77-4bdb-86a0-0c47b4316588&serverRelativeUrl=%2Fsites%2FBUASL%2FSitePages%2FCanvas.aspx&pagesListId=c6ceafdd-fe00-4696-ae0e-b228d804a1d9&locale=en-us&TeamsCID=27f05b70-7861-41fc-b9c5-a822a1eb5d6e&OR=Teams-HL&CT=1793278574515&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiI0OS8yNjEwMDIxMjAxOCIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D&xsdata=MDV8MDJ8fDczYjA1YzNiNjA1YTQxMzA0NDM0MDhkZjM1YmMxZWY0fDMwZjUyMzQ0NDY2MzRjMmViYWIzNjFiZjI0ZWJiZWQ4fDB8MHw2MzkyODg3NTQxOTkzNjA3MjB8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA9fDF8TDJOb1lYUnpMekU1T21FMU9tRTFPRG00WVdaakxUTTNabVF0TkRFNVlqVmhZakpsTFRKall6WTRORFpoTW1FMk0xOW1ZMlJoTmpCa1lTMDFNREF3TFRRMVpXWXRZamc0TmkxaU5qY3daRGxqTXpaaVptRkFkVzV4TG1kaWJDNXpjR0ZqWlhNdmJXVnpjeTl6YkdGblpYSXZNVGs1TXpJNE56STNPRFl4TnpjNU13PT18fNDQ2MmJhMTg3ZWMyNDJlYmU5OGYwOGRmMzViYzFlZjN8Yzk2ODY2MzM0YTQ3NDQwMmIxYTdhMTgzYjI2YWZmNzA%3D&sdata=Q24wbTdFWjh4dXVTVTBPM2ZnSEo4dEdaQmlUaVRRTVF4K3dMV0ExV3BJdz0%3D'

const AppContent = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
  useIPadKeyboardHandler()
  return <>{children}</>
}

const ReleaseNotes = (): React.JSX.Element | null => {
  const { accounts, instance } = useMsal()
  const environment = getEnv()
  const [hasActiveAccount, setHasActiveAccount] = useState(
    () => instance.getActiveAccount() !== null,
  )

  useEffect(() => {
    const account = instance.getActiveAccount() ?? accounts[0]
    if (!account) {
      setHasActiveAccount(false)
      return
    }

    instance.setActiveAccount(account)
    setHasActiveAccount(true)
  }, [accounts, instance])

  if (!environment || !hasActiveAccount) return null

  return (
    <system-version
      system="Assortment Canvas"
      env={environment === 'prod' ? 'prod' : 'dev'}
      more-info-url={RELEASE_NOTES_MORE_INFO_URL}
      msalInstance={instance}
    />
  )
}

const App = (): React.JSX.Element => {
  const { isOnline } = useNetwork()

  useMsalAuthentication(InteractionType.Redirect, loginRequest)

  useEffect(() => {
    if (!isOnline) {
      errorSnackBar(
        'Network connection lost',
        'Changes will not be saved until the connection is restored.',
      )
    } else createSnackBar(false)
  }, [isOnline])

  const [queryClient] = useState(() => new QueryClient())
  return (
    <DeviceProvider>
      <AppContent>
        <div className="app-container">
          <AuthenticatedTemplate>
            <ConfigProvider>
              <NetworkInterceptor>
                <QueryClientProvider client={queryClient}>
                  <BrowserRouter>
                    <Routes>
                      <Route index element={<Navigate to="/canvas/home" replace />} />
                      <Route path="/canvas">
                        <Route index element={<Navigate to="/home" replace />} />
                        <Route
                          path="home"
                          element={
                            <react.Suspense fallback={<div>Loading Home...</div>}>
                              <Home />
                            </react.Suspense>
                          }
                        />
                        <Route
                          path=":seasonId/:divisionId/:sectionId/:deptId/:canvasId/:isPresentReadMode?"
                          element={
                            <react.Suspense fallback={<div>Loading CanvasWrapper...</div>}>
                              <CanvasWrapper />
                            </react.Suspense>
                          }
                        />
                      </Route>
                      <Route path="*" element={<Navigate to="/canvas/home" replace />} />
                    </Routes>
                  </BrowserRouter>
                  {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
                </QueryClientProvider>
              </NetworkInterceptor>
              <SessionExpiredDialog />
              <ReleaseNotes />
            </ConfigProvider>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <div>Signing in...</div>
          </UnauthenticatedTemplate>
        </div>
      </AppContent>
    </DeviceProvider>
  )
}

export default App
